import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, TextField, Typography, Divider, useTheme } from "@mui/material";

// --- Mappings ---
const LETTER_MAP: Record<string, string> = {
  A: "أ",
  B: "ب",
  J: "ح",
  D: "د",
  R: "ر",
  S: "س",
  X: "ص",
  T: "ط",
  E: "ع",
  G: "ق",
  K: "ك",
  L: "ل",
  Z: "ز",
  N: "ن",
  H: "هـ",
  U: "و",
  V: "ى",
};

const NUMBER_MAP: Record<string, string> = {
  "0": "٠",
  "1": "١",
  "2": "٢",
  "3": "٣",
  "4": "٤",
  "5": "٥",
  "6": "٦",
  "7": "٧",
  "8": "٨",
  "9": "٩",
};

interface PlateData {
  en_plate_number: string;
  ar_plate_number: string;
}

interface PlateInputProps {
  value: PlateData;
  onChange: (value: PlateData) => void;
  onError: (message: string) => void;
}

/**
 * A Saudi plate is exactly 3 English letters + 4 digits (e.g. "ABC 1234").
 * Callers should pass the raw letters/numbers segments (not the combined
 * "LETTERS NUMBERS" string).
 */
export function isValidSaudiPlate(letters: string, numbers: string): boolean {
  return letters.length === 3 && numbers.length === 4;
}

export const PlateInput: React.FC<PlateInputProps> = ({ value, onChange, onError }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [letters, setLetters] = useState("");
  const [numbers, setNumbers] = useState("");

  const lettersRef = useRef<HTMLInputElement>(null);
  const numbersRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value && value.en_plate_number) {
      const split = value.en_plate_number.split(" ");
      setLetters(split[0] || "");
      setNumbers(split[1] || "");
    } else {
      setLetters("");
      setNumbers("");
    }
  }, [value.en_plate_number]);


  const generateUpdate = (newLetters: string, newNumbers: string) => {
    const arLetters = newLetters
      .split("")
      .map((char) => LETTER_MAP[char] || char)
      .join(" ");
    const arNumbers = newNumbers
      .split("")
      .map((num) => NUMBER_MAP[num] || num)
      .join("");

    onChange({
      en_plate_number: `${newLetters} ${newNumbers}`.trim(),
      ar_plate_number: `${arLetters} ${arNumbers}`.trim(),
    });
  };

  const handleLettersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawInput = e.target.value.toUpperCase();

    if (rawInput.length < letters.length) {
      setLetters(rawInput);
      generateUpdate(rawInput, numbers);
      return;
    }

    const cleanInput = rawInput.replace(/[^A-Z]/g, "");
    const lastChar = cleanInput.slice(-1);

    if (cleanInput.length > 0 && !LETTER_MAP[lastChar]) {
      onError(`The letter '${lastChar}' is not valid in Saudi License Plates.`);
      return;
    }

    const finalVal = cleanInput.slice(0, 3);
    setLetters(finalVal);
    generateUpdate(finalVal, numbers);

    if (finalVal.length === 3) {
      numbersRef.current?.focus();
    }
  };

  const handleNumbersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
    setNumbers(val);
    generateUpdate(letters, val);

    if (val.length === 4 && !isValidSaudiPlate(letters, val)) {
      onError(t('transportation.fleet.invalidPlate'));
    }
  };

  const getArabicDisplay = (text: string, type: "letters" | "numbers") => {
    if (!text) return "-";
    return text
      .split("")
      .map((char) =>
        type === "letters" ? LETTER_MAP[char] || "?" : NUMBER_MAP[char] || char
      )
      .join(" ");
  };
  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, display: "block" }}
      >
        Plate Number
      </Typography>
      <Box
        sx={{
          display: "flex",
          border: `2px solid ${theme.palette.text.primary}`,
          borderRadius: "8px",
          overflow: "hidden",
          backgroundColor: "#00000030",
          height: "80px",
        }}
      >
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              inputRef={lettersRef}
              value={letters}
              onChange={handleLettersChange}
              placeholder={t('components.plateInput.lettersPlaceholder')}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: "1.4rem",
                  letterSpacing: "0.3rem",
                  fontWeight: "bold",
                  textAlign: "center",
                  height: "100%",
                },
              }}
              inputProps={{
                style: {
                  textAlign: "center",
                  textTransform: "uppercase",
                  paddingTop: "8px",
                },
                maxLength: 3,
              }}
              fullWidth
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#FFFFFF10",
              borderTop: "1px dashed #e0e0e0",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", letterSpacing: "0.3rem" }}
            >
              {getArabicDisplay(letters, "letters")}
            </Typography>
          </Box>
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ width: "2px", bgcolor: "text.primary" }}
        />

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              inputRef={numbersRef}
              value={numbers}
              onChange={handleNumbersChange}
              placeholder={t('components.plateInput.numbersPlaceholder')}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: "1.4rem",
                  letterSpacing: "0.3rem",
                  fontWeight: "bold",
                  textAlign: "center",
                  height: "100%",
                },
              }}
              inputProps={{
                dir: "ltr",
                inputMode: "numeric",
                style: {
                  direction: "ltr",
                  unicodeBidi: "isolate",
                  textAlign: "center",
                  paddingTop: "8px",
                },
                maxLength: 4,
              }}
              fullWidth
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#FFFFFF10",
              borderTop: "1px dashed #e0e0e0",
            }}
          >
            <Typography
              variant="h6"
              dir="ltr"
              sx={{
                direction: "ltr",
                unicodeBidi: "isolate",
                fontWeight: "bold",
                letterSpacing: "0.3rem",
              }}
            >
              {getArabicDisplay(numbers, "numbers")}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: "#000000",
            width: "40px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderLeft: `1px solid ${theme.palette.divider}`,
            color: "white",
            p: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: "bold",
              transform: "rotate(90deg)",
              whiteSpace: "nowrap",
              fontSize: "0.6rem",
            }}
          >
            KSA
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
